import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AgentLaunchLogs = () => {
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const agentOptions = [
    { value: 'all', label: 'All Agents' },
    { value: 'proposalbot', label: 'ProposalBot' },
    { value: 'insightminer', label: 'InsightMiner' },
    { value: 'efficiency-auditor', label: 'Efficiency Auditor' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'success', label: 'Success' },
    { value: 'running', label: 'Running' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const launchLogs = [
    {
      id: 1,
      agentType: "InsightMiner",
      agentName: "Market Research Agent #47",
      status: "success",
      startTime: "2024-10-29T14:30:00Z",
      endTime: "2024-10-29T14:45:00Z",
      duration: "15m 23s",
      environment: "production",
      user: "john.doe@ipsos.com",
      dataProcessed: "2,847 records",
      insights: 156,
      errors: 0
    },
    {
      id: 2,
      agentType: "ProposalBot",
      agentName: "Proposal Generator #23",
      status: "running",
      startTime: "2024-10-29T15:10:00Z",
      endTime: null,
      duration: "Running for 8m 12s",
      environment: "production",
      user: "sarah.wilson@ipsos.com",
      dataProcessed: "1,234 records",
      insights: 89,
      errors: 0
    },
    {
      id: 3,
      agentType: "Efficiency Auditor",
      agentName: "Process Optimizer #12",
      status: "failed",
      startTime: "2024-10-29T13:45:00Z",
      endTime: "2024-10-29T13:52:00Z",
      duration: "7m 18s",
      environment: "sandbox",
      user: "mike.chen@ipsos.com",
      dataProcessed: "456 records",
      insights: 0,
      errors: 3
    },
    {
      id: 4,
      agentType: "InsightMiner",
      agentName: "Consumer Sentiment Analyzer #89",
      status: "success",
      startTime: "2024-10-29T12:20:00Z",
      endTime: "2024-10-29T12:38:00Z",
      duration: "18m 45s",
      environment: "production",
      user: "emma.taylor@ipsos.com",
      dataProcessed: "5,623 records",
      insights: 234,
      errors: 1
    },
    {
      id: 5,
      agentType: "ProposalBot",
      agentName: "Client Proposal #156",
      status: "cancelled",
      startTime: "2024-10-29T11:15:00Z",
      endTime: "2024-10-29T11:18:00Z",
      duration: "3m 12s",
      environment: "sandbox",
      user: "alex.rodriguez@ipsos.com",
      dataProcessed: "89 records",
      insights: 0,
      errors: 0
    }
  ];

  const filteredLogs = launchLogs?.filter(log => {
    const agentMatch = selectedAgent === 'all' || log?.agentType?.toLowerCase()?.replace(' ', '-') === selectedAgent;
    const statusMatch = selectedStatus === 'all' || log?.status === selectedStatus;
    return agentMatch && statusMatch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'CheckCircle';
      case 'running': return 'Clock';
      case 'failed': return 'XCircle';
      case 'cancelled': return 'StopCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'running': return 'text-warning';
      case 'failed': return 'text-destructive';
      case 'cancelled': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getEnvironmentBadge = (env) => {
    return env === 'production' ?'bg-success/10 text-success' :'bg-warning/10 text-warning';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Agent Launch Logs</h3>
          <p className="text-sm text-text-secondary mt-1">
            Monitor agent deployment history and execution details
          </p>
        </div>
        <Button variant="outline" iconName="Download">
          Export Logs
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Filter by Agent Type"
          options={agentOptions}
          value={selectedAgent}
          onChange={setSelectedAgent}
        />
        <Select
          label="Filter by Status"
          options={statusOptions}
          value={selectedStatus}
          onChange={setSelectedStatus}
        />
      </div>
      <div className="space-y-4">
        {filteredLogs?.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-lg">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Logs Found</h4>
            <p className="text-text-secondary">
              No agent launch logs match the selected filters
            </p>
          </div>
        ) : (
          filteredLogs?.map((log) => (
            <div key={log?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon 
                      name={getStatusIcon(log?.status)} 
                      size={20} 
                      className={getStatusColor(log?.status)}
                    />
                    <h4 className="text-md font-semibold text-card-foreground">{log?.agentName}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEnvironmentBadge(log?.environment)}`}>
                      {log?.environment}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Agent Type:</span>
                      <p className="text-card-foreground font-medium">{log?.agentType}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Duration:</span>
                      <p className="text-card-foreground font-medium">{log?.duration}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Data Processed:</span>
                      <p className="text-card-foreground font-medium">{log?.dataProcessed}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Insights Generated:</span>
                      <p className="text-card-foreground font-medium">{log?.insights}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mt-4">
                    <div>
                      <span className="text-text-secondary">Started:</span>
                      <p className="text-card-foreground font-medium">
                        {new Date(log.startTime)?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">User:</span>
                      <p className="text-card-foreground font-medium">{log?.user}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Errors:</span>
                      <p className={`font-medium ${log?.errors > 0 ? 'text-destructive' : 'text-success'}`}>
                        {log?.errors}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Icon name="Eye" size={16} />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Icon name="Download" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AgentLaunchLogs;